/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import RightIcon from '../../assets/rightIcon.png';
import './styles.scss';

interface BreadcrumbItem {
  pathname: string
  text: string
  href?: string
}

interface BreadcrumbsProps {
  rootPage: string
}

const capitalizeBreadcrumbText = (text: string) => {
  return `${text.charAt(0).toUpperCase() + text.slice(1)}`;
};

const formatRootPageName = (rootPage: string) => {
  const formattedRootPageName = rootPage.replace(/([A-Z])/g, ' $1').split(' ').map((namePart) => capitalizeBreadcrumbText(namePart)).join(' ');
  return formattedRootPageName;
};

const prepareBreadcrumbs = (rootPage: string): BreadcrumbItem[] => {
  const path = window.location.pathname;
  const items = [...path.split('/')].filter((item) => item !== '' && item !== rootPage);
  const { t } = useTranslation();

  const breadcrumbsItems = items.map((item, index) => {
    const pathname = `${path.split('/').splice(0, 3).join('/')}`;
    const newItem: BreadcrumbItem = {
      text: '',
      pathname,
    };

    if ((index < items.length - 1) && (index > 1)) {
      newItem.href = `${item}`;
    }

    switch (item) {
      case 'create':
        newItem.text = t('new.breadCrumb');
        break;
      case 'networkId':
        newItem.text = t('networkId.breadCrumb');
        break;
      case 'rpoptions':
        newItem.text = t('common.menu.radiusParserOptions');
        break;
      // case 'ssh-info':
      //   newItem.text = t('common.menu.sshSecurityInfo');
      //   break;
      case 'iri-buffering':
        newItem.text = t('common.menu.iriBuffering');
        break;
      case 'tls-profile':
        newItem.text = t('tlsProfile.breadCrumb');
        break;
      case 'template-per-access-function':
        newItem.text = t('common.menu.targetDeliveryTemplatePerAccessFunction');
        break;
      case 'utmpktcfcis':
        newItem.text = t('common.menu.utimacoPacketSwitch');
        break;
      case 'utmisdncfcis':
        newItem.text = t('common.menu.utimacoCircuitSwitch');
      break;
      case 'periodic-audit':
        newItem.text = t('periodicAFAudit.breadCrumb');
        break;
      case 'process-options':
        newItem.text = t('processOptions.breadCrumb');
        break;
      case 'cf-groups':
        newItem.text = t('common.menu.collectionFunctionGroups');
        break;
      case 'csp-configs':
        newItem.text = t('common.menu.cspConfigs');
        break;
      case 'dipms':
        newItem.text = t('dialedDigitExtractionServer.breadCrumb');
        break;
      case 'distributors':
        newItem.text = t('common.menu.distributors');
        break;
      case 'targetTemplate':
        newItem.text = t('audit.userAudit.grid.targetTempBreadCrum');
        break;
      case 'systemConfiguration':
        newItem.text = t('common.menu.systemConfiguration');
        break;
      case 'target-domain':
        newItem.text = t('common.menu.targetDomain');
        break;
      case 'xtp':
        newItem.text = t('common.menu.xtp');
        break;
      case 'xdp':
        newItem.text = t('common.menu.xdp');
        break;
      case 'connection-between-xtp-and-xdp':
        newItem.text = t('common.menu.xtpXdpConnection');
        break;
      case 'nat-configuration':
        newItem.text = 'NAT Configuration';
        break;
      case 'cf-ftp-infos':
        newItem.text = t('cfFtpInfo.breadCrumb');
        break;
      case 'jurisdiction-area':
        newItem.text = t('jurisdictionArea.breadcrumb');
        break;
      case 'filter-setting':
        newItem.text = t('filterSetting.breadCrumb');
        break;
      case 'filter-group':
        newItem.text = t('filterGroup.breadCrumb');
        break;
      case 'af-filter':
        newItem.text = t('afFilter.breadCrumb');
        break;
      case 'sip-setting':
        newItem.text = t('sipSetting.breadCrumb');
        break;
      case 'registered-nodes':
        newItem.text = t('registeredNodes.breadCrumb');
        break;
      case 'backup-and-restore':
        newItem.text = t('backupRestore.breadCrumb');
        break;
      case 'fqdn':
        newItem.text = t('fqdn.breadCrumb');
        break;
      case 'radius-parser-password':
        newItem.text = t('common.menu.radiusParserPassword');
        break;
      case 'media-gateway':
        newItem.text = t('mediaGateway.breadCrumb');
        break;
      case 'alarm-notification-profile':
        newItem.text = t('alarmNotificationProfile.breadCrumb');
        break;
      case 'xcca':
        newItem.text = t('xcca.breadcrumb');
        break;
      case 'xcca-dashboard':
        newItem.text = t('xccaDashboard.breadcrumb');
        break;
      case 'snmp-users':
        newItem.text = t('common.menu.snmpUsers');
        break;
      case 'snmp-destinations':
        newItem.text = t('common.menu.snmpDestinations');
        break;
      default:
        newItem.text = capitalizeBreadcrumbText(item);
        break;
    }

    return newItem;
  });

  return breadcrumbsItems;
};

const checkhref = (href: any, pathname: string, text: string) => {
  if (href === 'iri-buffering' || href === 'periodic-audit') {
    return (
      <Link
        className="spanLink"
        to={{
          pathname: `${pathname}/${href}/view`,
        }}
      >
        {text}
      </Link>
    );
  }
  return (
    <Link
      className="spanLink"
      to={{
        pathname: `${pathname}/${href}`,
      }}
    >
      {text}
    </Link>
  );
};

const CustomBreadcrumbs = ({ rootPage }: BreadcrumbsProps): JSX.Element => {
  const breadcrumbsItems = prepareBreadcrumbs(rootPage);
  const formattedRootPageName = formatRootPageName(rootPage);

  return (
    <div className="breadCrumb">
      <span className="firstSpan">{formattedRootPageName}</span>
      {breadcrumbsItems.map((item) => {
        return (
          <React.Fragment key={`${item.text}${item.pathname}`}>
            <img src={RightIcon} style={{ paddingLeft: 10, paddingRight: 10 }} alt="icon" />
            {item.href ? (
              checkhref(item.href, item.pathname, item.text)) :
              <span className="spanLink">{item.text}</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CustomBreadcrumbs;
